import { ChangeEvent } from 'react';
import clsx from 'clsx';
import { IconButton } from './IconButton';
import { LayoutSwitcher } from './LayoutSwitcher';
import { useDashboardStore, GridLayout } from '@store/dashboardStore';

interface Props {
  totalCount: number;
  visibleCount: number;
  onAddCamera: () => void;
  onManageGroups: () => void;
  canManageCameras: boolean;
  canManageGroups: boolean;
}

export function Toolbar({
  totalCount,
  visibleCount,
  onAddCamera,
  onManageGroups,
  canManageCameras,
  canManageGroups,
}: Props): JSX.Element {
  const { filters, layout, setSearch, toggleFavoritesOnly, setLayout, resetFilters } =
    useDashboardStore();

  const handleSearch = (e: ChangeEvent<HTMLInputElement>): void => setSearch(e.target.value);
  const handleLayout = (next: GridLayout): void => setLayout(next);

  return (
    <header className="sticky top-0 z-10 border-b border-border bg-bg/80 backdrop-blur">
      <div className="flex flex-wrap items-center gap-3 px-6 py-4">
        <div>
          <h1 className="text-lg font-semibold">Live Dashboard</h1>
          <p className="text-xs text-muted">
            {visibleCount} of {totalCount} cameras
          </p>
        </div>

        <div className="ml-auto flex flex-1 flex-wrap items-center justify-end gap-3">
          {canManageGroups ? (
            <button
              type="button"
              onClick={onManageGroups}
              className={clsx(
                'rounded-md border border-border bg-surface px-3 py-1.5 text-xs font-medium text-foreground',
                'transition-colors hover:border-accent',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent',
              )}
            >
              Manage groups
            </button>
          ) : null}

          {canManageCameras ? (
            <button
              type="button"
              onClick={onAddCamera}
              className={clsx(
                'rounded-md bg-accent px-3 py-1.5 text-xs font-semibold text-white',
                'transition-colors hover:bg-accent-hover',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent',
              )}
            >
              + Add camera
            </button>
          ) : null}

          <label className="relative w-full max-w-xs">
            <span className="sr-only">Search cameras</span>
            <input
              type="search"
              value={filters.search}
              onChange={handleSearch}
              placeholder="Search by name…"
              className={clsx(
                'h-9 w-full rounded-md border border-border bg-surface px-3 pr-9 text-sm',
                'placeholder:text-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent',
              )}
            />
            <span aria-hidden className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-muted">
              ⌕
            </span>
          </label>

          <IconButton
            label={filters.favoritesOnly ? 'Show all cameras' : 'Show favorites only'}
            active={filters.favoritesOnly}
            onClick={toggleFavoritesOnly}
          >
            <span className={filters.favoritesOnly ? 'text-warning' : ''}>★</span>
          </IconButton>

          <button
            type="button"
            onClick={resetFilters}
            className={clsx(
              'rounded-md border border-border bg-surface px-3 py-1.5 text-xs font-medium text-muted',
              'transition-colors hover:border-accent hover:text-foreground',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent',
            )}
          >
            Reset
          </button>

          <LayoutSwitcher value={layout} onChange={handleLayout} />
        </div>
      </div>
    </header>
  );
}
